/**
 * ChatAttachmentTile component for the Indigo design system.
 *
 * A square thumbnail for an attachment staged in a chat composer. Images fill
 * the tile; other files fall back to an icon over a truncated filename. Use
 * this above a chat input where the attachment itself is the content the user
 * needs to recognize — `ChatPromptFile` remains the right choice when the
 * filename is the primary information.
 *
 * @example
 * ```tsx
 * <ChatAttachmentTile
 *   previewUrl={objectUrl}
 *   filename="scan.png"
 *   onRemove={() => remove(i)}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Icon } from "../../Icons";
import { Tooltip } from "../../Tooltip";
import "./ChatAttachmentTile.css";

export type ChatAttachmentTileSize = "sm" | "lg";

export interface ChatAttachmentTileProps {
  /** Image source. When omitted the tile renders the document variant. */
  previewUrl?: string;
  /** Alt text for images; the visible label for documents. */
  filename: string;
  /** Icon for the document variant. Defaults to a generic file icon. */
  icon?: ReactNode;
  /**
   * `sm` (default) is a fixed 64×64 square for composer staging. `lg` sizes to
   * the image's own aspect ratio, for showing a sent attachment in a message.
   */
  size?: ChatAttachmentTileSize;
  /** Dims the tile behind a spinner — e.g. while the file uploads on send. */
  loading?: boolean;
  /** Remove action, revealed on hover/focus. */
  onRemove?: () => void;
  /** Accessible label for the remove action. */
  removeLabel?: string;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatAttachmentTile = forwardRef<
  HTMLDivElement,
  ChatAttachmentTileProps
>(
  (
    {
      previewUrl,
      filename,
      icon,
      size = "sm",
      loading,
      onRemove,
      removeLabel,
      className,
      ...rest
    },
    ref,
  ) => (
    // The image is the content; the filename is the detail, so it lives in a
    // tooltip. Uses the design-system Tooltip rather than a native `title` —
    // that one waits ~1s and renders in OS chrome, so it reads as missing.
    <Tooltip content={filename} placement="top" delay={150}>
      <div
        ref={ref}
        className={clsx(
          "indigo-chat-attachment-tile",
          `indigo-chat-attachment-tile--${size}`,
          className,
        )}
        data-testid={rest["data-testid"]}
      >
        {previewUrl ? (
          <img
            className="indigo-chat-attachment-tile__image"
            src={previewUrl}
            alt={filename}
          />
        ) : (
          <div className="indigo-chat-attachment-tile__file">
            {icon ?? <Icon name="file" sizeX={18} />}
            <span
              className="indigo-chat-attachment-tile__filename"
              title={filename}
            >
              {filename}
            </span>
          </div>
        )}

        {loading && (
          <span
            className="indigo-chat-attachment-tile__loading"
            role="status"
            aria-label={`Uploading ${filename}`}
          >
            <span className="indigo-chat-attachment-tile__spinner" />
          </span>
        )}

        {onRemove && !loading && (
          <button
            type="button"
            className="indigo-chat-attachment-tile__remove"
            aria-label={removeLabel ?? `Remove ${filename}`}
            onClick={onRemove}
          >
            <Icon name="close" sizeX={10} />
          </button>
        )}
      </div>
    </Tooltip>
  ),
);

ChatAttachmentTile.displayName = "ChatAttachmentTile";

export default ChatAttachmentTile;
